import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ClientDTO,
  saveLead,
  ClientContactDTO,
  ILead,
  ourEntityAllList,
  IOurEntity,
  getBusinessUnit,
  LogAction,
  LogContextType,
  saveActivityLog,
  getAllLeadStatusByVisibility,
  getUserByEmail,
  assignClientUser,
  assignClientContact,
  ClientContactShareMultipleDTO,
  LeadStatusDTO,
  FieldTypeEnum,
  ICustomField,
} from 'wisestep-api';
import { ControlledTextArea, ControlledTextInput, Label, WsButton, WsSelect } from 'wisestep-base';

import { checkIfRequiredField } from '../../../components/BaseCustomField/CustomFieldHelper';
import ClientAutoCompleteFilter from '../../../components/clientAutoCompleteFilter';
import ClientContactAutoComplete from '../../../components/clientContactAutoComplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import WsTagsFilter from '../../../components/filters/components/WsTagsFilter';
import { getAvEditAddDesUiName } from '../../../features/activityLog/ActivityHelper';
import CustomField from '../../../features/clientListingPage/components/CustomField';
import CreateNewClientContactDialog from '../../../features/jobs/post/components/CreateNewClientContactDialog';
import CreateNewClientDialog from '../../../features/jobs/post/components/CreateNewClientDialog';
import IBusinessUnit from '../../../features/ourEntity/types/IBusinessUnit';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { defaultLeadActivityLogDTO, getValidationSchema } from '../helper/leadHelper';

export interface LeadDetailsFormProps {
  setFormWizardCurrentStep?: (step: number) => void;
  defaultLeadData?: ILead;
  postSubmit?: (lead: ILead) => void;
  customFields: ICustomField[];
  defaultFields: ICustomField[];
}

export interface StatusList {
  label: string;
  value: string;
}

interface LeadFormValues {
  leadName: string;
  status: string;
  leadOwnerId: string;
  leadOwnerName: string;
  source?: string;
  companyName: string;
  clientRecruiter: string;
  businessUnitsInfo: string[];
  linkedinUrl?: string;
  tags?: string[];
  description?: string;
  organisationEntityId?: string;
  clientId?: string;
  clientContactId?: string;
  leadStatusId?: string;
}

const LeadDetailsForm = ({
  setFormWizardCurrentStep,
  defaultLeadData,
  postSubmit,
  customFields,
  defaultFields,
}: LeadDetailsFormProps) => {
  const [businessUnits, setBusinessUnits] = useState<IBusinessUnit[]>([]);
  const [statusList, updateStatusList] = useState<StatusList[]>([]);
  const [defaultLeadStatus, setDefaultLeadStatus] = useState<LeadStatusDTO>();

  const fetchBusinessUnits = async () => {
    const response = await getBusinessUnit();
    setBusinessUnits(response.data);
  };

  const getStatusList = async () => {
    const response = await getAllLeadStatusByVisibility();
    const statusData: StatusList[] = [];
    let defaultStatusName = '';

    response.data.forEach((obj: LeadStatusDTO) => {
      methods.setValue('leadStatusId', obj.id);
      defaultStatusName = obj.id;
      setDefaultLeadStatus(obj);
      statusData.push({
        label: obj.leadStatusName as string,
        value: obj.id ?? '',
      });
    });

    if (defaultLeadData?.leadStatus?.leadStatusName) {
      const selectedOption = statusData.find(
        (item) => item.label === defaultLeadData?.leadStatus?.leadStatusName
      );
      if (selectedOption) {
        methods.setValue('leadStatusId', selectedOption.value as string);
      }
    } else {
      methods.setValue('leadStatusId', defaultStatusName);
    }

    statusData.sort((a, b) => {
      const aLabel = a.label.trim().toLowerCase();
      const bLabel = b.label.trim().toLowerCase();
      const aIsOpen = aLabel === 'open';
      const bIsOpen = bLabel === 'open';

      if (aIsOpen && !bIsOpen) return -1;
      if (!aIsOpen && bIsOpen) return 1;
      return aLabel.localeCompare(bLabel);
    });

    updateStatusList(statusData);
  };

  useEffect(() => {
    fetchBusinessUnits();
    getStatusList();
  }, []);

  const selectOptions = useMemo(
    () => businessUnits.map((bu) => ({ label: bu.name, value: bu.id })),
    [businessUnits]
  );

  const getBusinessUnitData = useMemo(() => {
    if (!defaultLeadData?.businessUnitsInfo?.buinfo) return [];
    return defaultLeadData.businessUnitsInfo.buinfo.map((bu) => bu.id);
  }, [defaultLeadData]);

  const methods = useForm<LeadFormValues>({
    resolver: yupResolver(getValidationSchema(defaultFields || [])),
    defaultValues: {
      ...defaultLeadData,
      companyName: defaultLeadData?.client?.name,
      clientRecruiter: defaultLeadData?.clientContact?.emailAddress,
      leadOwnerId: defaultLeadData?.leadOwnerInfo?.id || '',
      leadOwnerName: defaultLeadData?.leadOwnerInfo?.name || '',
      businessUnitsInfo: getBusinessUnitData,
      tags: defaultLeadData?.tags ?? [],
      leadStatusId: defaultLeadData?.leadStatus?.id || '',
    },
  });

  useEffect(() => {
    if (businessUnits?.length > 0) {
      methods.setValue('businessUnitsInfo', getBusinessUnitData, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    if (defaultLeadData?.leadOwnerInfo?.id) {
      methods.setValue('leadOwnerId', defaultLeadData.leadOwnerInfo.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [defaultLeadData, businessUnits, getBusinessUnitData, methods]);

  const {
    formState: { dirtyFields },
  } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isLoading, setIsLoading] = useState(false);

  const [companyNameNewVal, setCompanyNameNewVal] = useState<string>('');
  const [openNewClientDialogue, setOpenNewClientDialogue] = useState<boolean>(false);
  const [isMarkInactive, setIsMarkInactive] = useState<boolean>(false);
  const [openClientContactDialog, setOpenClientContactDialog] = useState<boolean>(false);
  const [companyContactInput, setCompanyContactInput] = useState<string>('');
  const [ourEntities, setOurEntities] = useState<IOurEntity[]>([]);

  const navigate = useNavigate();
  const { handleSubmit, setValue, watch } = methods;

  const companyName = watch('companyName') ?? '';

  const enteredRecruiter = watch('clientRecruiter') ?? '';
  const data = enteredRecruiter.includes('|') ? enteredRecruiter.split('|')[0] : enteredRecruiter;
  const clientRecruiter = data?.split(' - ')[1] ?? data;

  useEffect(() => {
    const fetchData = async () => {
      const response = await ourEntityAllList();
      setOurEntities(response.data.data);
    };
    fetchData();
  }, [setOurEntities]);

  useEffect(() => {
    if (isEmpty(companyName?.trim())) setValue('clientRecruiter', '');
  }, [enteredRecruiter, companyName, setValue]);

  const selectedEmail = watch('leadOwnerId') ?? '';

  useEffect(() => {
    if (selectedEmail) {
      getUserByEmail(selectedEmail)
        .then((res) => {
          const firstName = res.data.firstName || '';
          const lastName = res.data.lastName || '';
          const recruiterName = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
          setValue('leadOwnerName', recruiterName);
        })
        .catch(() => {
          setValue('leadOwnerName', '');
        });
    } else {
      setValue('leadOwnerName', '');
    }
  }, [selectedEmail, setValue]);

  const onSubmit = async (data: ILead) => {
    setIsLoading(true);
    try {
      const buIds = Array.isArray(data.businessUnitsInfo) ? data.businessUnitsInfo : [];
      const businessUnitsArray = buIds.map((id) => {
        const matched = selectOptions.find((opt) => opt.value === id);
        return {
          id: id,
          name: matched ? matched.label : '',
        };
      });
      const selectedOrganisation = ourEntities.find((oe) => oe.id === data.organisationEntityId);
      const organisationEntityName = selectedOrganisation ? selectedOrganisation.Name : '';
      const paramsVal: ILead = {
        leadName: data?.leadName,
        source: data?.source ?? '',
        status: data?.status ?? '',
        leadOwnerInfo: {
          id: data?.leadOwnerId ?? null,
          name: data?.leadOwnerName ?? null,
        },
        businessUnitsInfo: {
          buinfo: businessUnitsArray,
        },
        organisationEntityId: data?.organisationEntityId ?? '',
        organisationEntityName: organisationEntityName ?? '',
        tags: data?.tags ?? [],
        linkedinUrl: data?.linkedinUrl ?? '',
        leadStatus: data?.leadStatusId
          ? { id: data.leadStatusId }
          : data?.leadStatus
          ? { id: data?.leadStatus?.id }
          : '',
        client: data?.clientId
          ? { id: data.clientId }
          : data?.client
          ? { id: data?.client?.id }
          : '',
        clientContact: data?.clientContactId
          ? { id: data.clientContactId }
          : data?.clientContact
          ? { id: data?.clientContact?.id }
          : '',
        description: data?.description ?? '',
        customFields: data?.customFields ?? {},
      };
      if (defaultLeadData?.id) {
        paramsVal.id = defaultLeadData.id;
      }

      const response = await saveLead(paramsVal);

      const clientShareDTO = {
        clientIds: [data?.clientId ?? data?.client?.id],
        userList: data?.leadOwnerId,
      };
      await assignClientUser(clientShareDTO);

      const contactShareDTO: ClientContactShareMultipleDTO = {
        clientId: data.clientId ?? (data?.client?.id as string),
        clientContactIds: [data?.clientContactId ?? data?.clientContact?.id],
        userList: data?.leadOwnerId,
      };
      await assignClientContact(contactShareDTO);

      if (response?.status === 200) {
        const avPayload = { ...defaultLeadActivityLogDTO };
        const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
        if (data) {
          if (data.id) {
            avPayload.action = LogAction.Edit;
            const avEmptyEntityfields = Object.keys(
              Object.entries(defaultLeadData).reduce((acc, [k, v]) => {
                if (v === '' || v === null) acc[k] = v;
                return acc;
              }, {})
            );
            avPayload.description = `${getAvEditAddDesUiName(
              'Lead',
              avEmptyEntityfields,
              avChangedValueViaDirtyFileds
            )} under Lead  ${response.data.leadName}`;
          } else {
            avPayload.action = LogAction.Add;
            avPayload.description = `ADDED Lead ${response.data.leadName}`;
          }
          avPayload.parentEntitiyType = LogContextType.Lead;
          avPayload.entityId = response.data.id;
          avPayload.parentEntitiyId = response.data.id;
          avPayload.field = 'Lead';
          avPayload.entityName = response.data.leadName;
          avPayload.createdBy = response.data.createdBy;
          avPayload.createdByName = response.data.createdByName;
          avPayload.createdOn = response.data.createdOn;
          avPayload.workspaceId = response.data.workspaceId;
          avPayload.contextData = {
            leadId: response.data.id,
            linkedinUrl: response.data.linkedinUrl,
            organisationEntityName: response.data.organisationEntityName,
            organisationEntityId: response.data.organisationEntityId,
            source: response.data.source,
            status: response.data.status,
            tags: response.data.tags,
          };
        }
        setNotification(success('Lead added successfully'));
        postSubmit?.(response.data);
        avChangedValueViaDirtyFileds[0]?.length > 0 ? await saveActivityLog(avPayload) : '';
        navigate(`/leads/details?leadId=${response.data.id}`);
      }
    } catch (error) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setFormWizardCurrentStep && setFormWizardCurrentStep(1);
    setIsLoading(false);
  };

  const onCancel = () => navigate('/leads');

  const onClientCreateFromDialogue = (selectedClient: ClientDTO) => {
    setValue('companyName', selectedClient?.name);
    setValue('clientId', selectedClient.id);
    setOpenNewClientDialogue(false);
  };

  const onClientContactCreateFromDialog = (selectedClientRecruiter: ClientContactDTO) => {
    const fullName = selectedClientRecruiter?.firstName
      .concat(' ' + selectedClientRecruiter?.lastName)
      .trim();

    setValue('clientRecruiter', `${fullName} - ${selectedClientRecruiter.emailAddress}`);
    setValue('clientContactId', selectedClientRecruiter?.id ?? '');
    setValue('clientRecruiterName', fullName);
    setOpenClientContactDialog(false);
  };

  const handleNewValueCreate = (data: string) => {
    setValue('clientRecruiter', '');
    setOpenClientContactDialog(true);
    setCompanyContactInput(data);
  };

  const handleStatusUpdate = (data: string) => {
    setValue('companyName', ' ');
    setCompanyNameNewVal(data ?? '');
    setIsMarkInactive(true);
    setOpenNewClientDialogue(true);
  };

  const handleNewClientValueCreate = (data: string) => {
    setValue('companyName', ' ');
    setCompanyNameNewVal(data ?? '');
    setOpenNewClientDialogue(true);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1} spacing={2}>
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="leadName"
                  type="text"
                  fullWidth
                  labelText="Lead Name"
                  variant="outlined"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'leadName', true)}
                  placeholder="Ex. Alice"
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsSelect
                  key="status"
                  name="leadStatusId"
                  label="Status"
                  selectArray={statusList}
                  variant="outlined"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'leadStatusId', true)}
                  defaultValue={defaultLeadStatus?.leadStatus?.leadStatusName}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <Label>
                  Lead Owner
                  {checkIfRequiredField(defaultFields, 'leadOwnerId', true) && '*'}
                </Label>

                <Controller
                  name="leadOwnerId"
                  control={methods.control}
                  defaultValue={defaultLeadData?.leadOwnerInfo?.id || ''}
                  render={({ field }) => (
                    <RecruiterSuggestions
                      {...field}
                      defaultValue={field.value ? [field.value] : []}
                      onSelectMultipleValues={(emails: string[]) => {
                        const selectedEmail = emails[0] || '';
                        field.onChange(selectedEmail);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="source"
                  type="text"
                  fullWidth
                  labelText="Source"
                  variant="outlined"
                  placeholder="Ex. Referral"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'source', false)}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <ClientAutoCompleteFilter
                  key={companyName}
                  name="companyName"
                  label="Client"
                  clientIdFieldName="clientId"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'companyName', true)}
                  textForAddingNew="Client"
                  onNewValueCreate={handleNewClientValueCreate}
                  onStatusUpdateValue={handleStatusUpdate}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <ClientContactAutoComplete
                  key={clientRecruiter}
                  name="clientRecruiter"
                  label="Client Contact"
                  clientId={
                    methods.getValues('clientId') ?? defaultLeadData?.clientContact?.clientId ?? ''
                  }
                  disabled={isEmpty(companyName?.trim())}
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'clientRecruiter', true)}
                  textForAddingNew="Client Contact"
                  clientName={companyName}
                  clientContactIdFieldName="clientContactId"
                  onNewValueCreate={handleNewValueCreate}
                  component="job"
                />
              </Grid>
              {/* <Grid item p={1} sm={6}>
                <Controller
                  name="organisationEntityId"
                  control={methods.control}
                  defaultValue=""
                  render={({ field }) => (
                    <WsSelect
                      name="organisationEntityId"
                      label="Organization Entity"
                      selectArray={ourEntities
                        .filter((oe) => oe.id !== undefined)
                        .map((oe) => ({
                          label: oe.Name,
                          value: oe.id as string,
                        }))}
                      size="small"
                      defaultValue=""
                      onChangeValue={field.onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <Label>Business Unit</Label>
                <WsCheckmarkSelect
                  name="businessUnitsInfo"
                  options={selectOptions}
                  getLabel={(option) => option.label ?? ''}
                  getValue={(option) => option.value ?? ''}
                />
              </Grid> */}
              <Grid item p={1} sm={6}>
                <ControlledTextInput
                  name="linkedinUrl"
                  type="text"
                  fullWidth
                  labelText="Job URL"
                  variant="outlined"
                  placeholder="Enter URL"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'linkedinUrl', false)}
                />
              </Grid>
              <Grid item p={1} sm={6}>
                <WsTagsFilter
                  placeHolder="Type to Select Tags"
                  name="tags"
                  label="Tags"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'tags', false)}
                />
              </Grid>
              {customFields &&
                customFields.map(
                  (field) =>
                    field.visible && (
                      <Grid
                        item
                        key={field.id}
                        p={1}
                        sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 6}
                      >
                        <CustomField {...field} name={`customFields.${field.name}`} />
                      </Grid>
                    )
                )}
              <Grid item p={1} sm={12}>
                <ControlledTextArea
                  name="description"
                  labelText="Description*"
                  minRows={5}
                  placeholder="Enter Description"
                  showMandatorySymbol={checkIfRequiredField(defaultFields, 'description', false)}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container p={1} direction="row" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Box mr={2}>
              <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
                Cancel
              </WsButton>
            </Box>
            <Box>
              <WsButton
                variant="contained"
                size="medium"
                type="submit"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </WsButton>
            </Box>
          </Grid>
        </form>
      </FormProvider>
      {openNewClientDialogue && (
        <CreateNewClientDialog
          open={openNewClientDialogue}
          clientNameInputVal={companyNameNewVal}
          onClose={setOpenNewClientDialogue}
          onSelect={onClientCreateFromDialogue}
          isMarkInactive={isMarkInactive}
        />
      )}
      {openClientContactDialog && companyName && (
        <CreateNewClientContactDialog
          open={openClientContactDialog}
          clientId={methods.getValues('clientId') ?? defaultLeadData?.clientContact?.clientId ?? ''}
          clientName={companyName ?? ''}
          clientRecruiterEmailInputVal={companyContactInput ?? ''}
          onClose={() => setOpenClientContactDialog(false)}
          onSelect={onClientContactCreateFromDialog}
        />
      )}
    </>
  );
};

export default LeadDetailsForm;
